import { useEffect, useMemo, useRef, useState } from 'react';
import { Control, FormProvider, useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { SchemableFormNavigationContextProvider } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContextProvider';
import { useInfiniteProductMasterdata } from 'services/repositories/masterdata/masterdata';
import { DisplayModes } from 'services/webedi/types';
import { DataTableHandle } from 'components/DataTable';
import { DataTableProvider } from 'components/DataTable/contexts/DataTableContext';
import { LockedRowsContext } from 'components/DataTable/contexts/LockedRowsContext';
import { ConfirmDeletePopup } from 'components/Display/ConfirmDeletePopup/ConfirmDeletePopup';
import { ValidationErrors } from 'components/Form/ValidationErrors/ValidationErrors';
import { InfiniteScroll } from 'components/ListTable/InfiniteScroll/InfiniteScroll';
import { LoadingPage } from 'components/Loading/LoadingPage';
import { UnsavedChangesModal } from 'components/Navigation/UnsavedChangesModal/UnsavedChangesModal';
import { Page } from 'components/Page/Page';

import { useProductMasterdataMode } from './hooks/useProductMasterdataMode';
import { useProductMasterdataSave } from './hooks/useProductMasterdataSave';
import { SettingsSidebar } from '../SettingsSidebar/SettingsSidebar';
import { AddProductButton } from './components/AddProductButton/AddProductButton';
import { ProductMasterdataTable } from './components/ProductMasterdataTable/ProductMasterdataTable';
import { ProductMasterdataToolbar } from './components/ProductMasterdataToolbar/ProductMasterdataToolbar';
import { ProductValidationPopover } from './components/ProductValidationPopover/ProductValidationPopover';
import {
  flattenProducts,
  PRODUCT_KEPT_FIELD,
  ProductMasterdataFormValues,
  ProductRow,
  PRODUCTS_DATA_PREFIX,
  toProductRows,
} from './helpers/productRows';
import { rowIndexOf } from './helpers/productValidationErrors';
import { ProductValidationService } from './helpers/ProductValidationService';

const NO_ROWS: ReadonlySet<number> = new Set();

export const ProductMasterdata = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('filter[search]') ?? '';

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useInfiniteProductMasterdata({
    variables: { search: searchQuery || undefined },
  });

  const rows = useMemo(() => {
    const products = data?.pages.flatMap((page) => (searchQuery ? page.data : flattenProducts(page.data))) ?? [];
    return toProductRows(products);
  }, [data?.pages, searchQuery]);

  const formMethods = useForm<ProductMasterdataFormValues>({
    defaultValues: { [PRODUCTS_DATA_PREFIX]: [] },
  });
  // One per visit: it carries a failed save's server errors to the table's rows and cells.
  const validation = useMemo(() => new ProductValidationService(), []);
  const { mode, enterEditMode, cancelEdit, finishEdit } = useProductMasterdataMode(
    formMethods.reset,
    rows,
    searchQuery,
  );
  const {
    save,
    isSaving,
    nonInputErrors,
    endSession,
    isConfirmingDelete,
    pendingDeleteCount,
    confirmDelete,
    cancelDelete,
  } = useProductMasterdataSave(formMethods, rows, finishEdit, validation);

  // A cell's server error describes the value that was sent, so it goes as soon as the user edits
  // that cell. Rows being added or removed shifts every index below them (RHF reports that as a
  // change to the whole `products` array), after which no error can be trusted to still be on the
  // row it was about.
  //
  // Marking a product for deletion zeroes its `kept` field, which RHF does not count as a change
  // (`isDirty` stays false) and which no cell subscribes to, so which rows are marked is tracked
  // here: for the unsaved-changes guard, and to lock those rows' cells. A marked product is going
  // away, so whatever the server had against it is dropped.
  const { watch, getValues } = formMethods;
  const [markedRows, setMarkedRows] = useState<ReadonlySet<number>>(NO_ROWS);
  const tableRef = useRef<DataTableHandle>(null);
  useEffect(() => {
    const { unsubscribe } = watch((_, { name }) => {
      const isKeptField = Boolean(name?.endsWith(`.${PRODUCT_KEPT_FIELD}`));

      if (name === PRODUCTS_DATA_PREFIX) {
        validation.clearErrors();
      } else if (name && isKeptField) {
        validation.clearRowErrors(rowIndexOf(name));
      } else if (name) {
        validation.clearEditedField(name);
      }

      if (name === PRODUCTS_DATA_PREFIX || isKeptField) {
        setMarkedRows(indexesOfMarkedRows(getValues(PRODUCTS_DATA_PREFIX)));
      }
    });
    return unsubscribe;
  }, [watch, getValues, validation]);

  const handleSearchChange = (value: string) => {
    setSearchParams(value ? { 'filter[search]': value } : {});
  };

  // A failed save's error banner (and the save's bookkeeping about what already reached the
  // server) shouldn't outlive the edit it was about. Reacting to `mode` itself (rather than only
  // wiring this into Cancel) catches every way edit mode can end — Cancel, a successful save, and
  // `useProductMasterdataMode`'s own exit when the URL's search param changes out from under an
  // in-progress edit — instead of just the one a developer remembered to wire explicitly.
  useEffect(() => {
    if (mode === DisplayModes.view) {
      endSession();
      setMarkedRows(NO_ROWS);
    }
  }, [mode, endSession]);

  return (
    <Page stickyMode="full">
      <Page.Head title={t('common:mainMenu.settings')} />

      {/*
        Wraps the whole section, not just its children: `Page.Section` renders `bottomBanner` as a
        sibling of `children` (see Page.tsx), so `AddProductButton` only sees `DataTableProvider`'s
        `append`/`fields` if these providers are its ancestor through the *element* tree — i.e. by
        wrapping `Page.Section` itself — rather than nested inside the JSX passed as its children.
      */}
      <FormProvider {...formMethods}>
        <SchemableFormNavigationContextProvider
          service={validation}
          NavigableItemPopoverComponent={ProductValidationPopover}
        >
          {/*
            Keyed on `mode`, not just the inner `DataTable`: `DataTableProvider`'s `fields` (what
            the table actually renders) lag the raw form by up to `DEBOUNCE_DELAY` after a
            value-only change like Cancel's `reset()` — row *count* changes adopt immediately, but
            this isn't one. Remounting the provider on every mode switch throws that stale state
            away instead of waiting for the debounce to catch up, which is what let a
            just-discarded edit still be visible for a moment back in view mode.
          */}
          <DataTableProvider key={mode} name={PRODUCTS_DATA_PREFIX} cheapDirtyCheck>
            <LockedRowsContext.Provider value={markedRows}>
              <Page.Section
                fillHeight
                leftAside={<SettingsSidebar />}
                // Sticky, below the table, rather than the table's own `footerMenu`: that only
                // appends below the table's own scroll box, so on a long catalogue it — and the
                // row it just added — ended up below the fold until the user scrolled the page
                // again.
                bottomBanner={
                  mode === DisplayModes.edit ? (
                    <AddProductButton defaultCurrency={rows[0]?.currency} disabled={isSaving} tableRef={tableRef} />
                  ) : undefined
                }
              >
                <div className="flex h-full min-h-0 flex-col">
                  <ProductMasterdataToolbar
                    mode={mode}
                    searchQuery={searchQuery}
                    onSearchChange={handleSearchChange}
                    onEnterEditMode={enterEditMode}
                    onCancelEdit={cancelEdit}
                    onSave={save}
                    validation={validation}
                    isSaving={isSaving}
                    isLoading={isLoading}
                  />

                  <ValidationErrors errors={nonInputErrors} />

                  {isLoading ? (
                    <LoadingPage />
                  ) : (
                    // `min-h-0` so this flex item can shrink below the table's intrinsic content
                    // size — without it, a flex child defaults to `min-height: auto`, which would
                    // let the table push the column past the viewport instead of scrolling
                    // internally.
                    <div className="min-h-0 flex-1">
                      <ProductMasterdataTable
                        mode={mode}
                        emptyState={t('settings:productMasterdata.emptyState')}
                        isSubmitting={isSaving}
                        tableRef={tableRef}
                        // Infinite loading is view-mode only: refetching mid-edit would discard
                        // unsaved rows. Edit mode's add-product button lives in the section's
                        // bottomBanner instead (see above).
                        footerMenu={
                          mode === DisplayModes.view && (hasNextPage || isFetchingNextPage) ? (
                            <InfiniteScroll
                              hasMore={Boolean(hasNextPage)}
                              loadMore={fetchNextPage}
                              loading={isFetchingNextPage}
                              noMoreItemsEnabled={false}
                            />
                          ) : undefined
                        }
                      />
                    </div>
                  )}
                </div>
              </Page.Section>
            </LockedRowsContext.Provider>
          </DataTableProvider>
        </SchemableFormNavigationContextProvider>
      </FormProvider>

      <ConfirmDeletePopup
        open={isConfirmingDelete}
        onClose={cancelDelete}
        onDelete={confirmDelete}
        title={t('settings:productMasterdata.deleteConfirm.title', { count: pendingDeleteCount })}
        description={t('settings:productMasterdata.deleteConfirm.description', { count: pendingDeleteCount })}
        deleteText={t('settings:productMasterdata.deleteConfirm.confirm')}
      />

      <UnsavedChangesGuard
        control={formMethods.control}
        isEditing={mode === DisplayModes.edit}
        hasMarkedRows={markedRows.size > 0}
      />
    </Page>
  );
};

/**
 * Reads `isDirty` here, not in the page: the first edit flips it, and a page that reads it
 * re-renders with everything below it — every cell of the table, through the form and navigation
 * contexts, whose values are rebuilt on each page render — just to learn something only this needs.
 */
const UnsavedChangesGuard = ({
  control,
  isEditing,
  hasMarkedRows,
}: {
  control: Control<ProductMasterdataFormValues>;
  isEditing: boolean;
  hasMarkedRows: boolean;
}) => {
  const { isDirty } = useFormState({ control });

  return <UnsavedChangesModal shouldBlock={isEditing && (isDirty || hasMarkedRows)} />;
};

const indexesOfMarkedRows = (rows: Array<ProductRow>): ReadonlySet<number> => {
  const marked = rows.flatMap((row, index) => (row[PRODUCT_KEPT_FIELD] === 0 ? [index] : []));
  return marked.length === 0 ? NO_ROWS : new Set(marked);
};
